import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import useSlideShowQuestionGroups from '@/hooks/api/useSlideShowQuestionGroups';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import { toCamelCaseDeep } from '@/modules/TCIUtils';
import useFlags from '@/hooks/useFlags';
import SectionNotebookSelects from './SectionNotebookSelects';
import UnitNotebookSelects from './UnitNotebookSelects';
import LessonNotebookSelects from './LessonNotebookSelects';
import ReadingCheckSelects from './ReadingCheckSelects';
import styles from '../index.module.scss';
import { slideObjectPropTypes } from '../../../../Utils';

type QuestionSelectFieldsProps = {
  exerciseId?: number,
  lessonId?: number,
  modelId: string;
  modelType: string;
  programId: number,
  sectionId: number,
  setExerciseId: (exerciseId: number) => void,
  setHasError: (error: boolean) => void,
  setSectionId: (sectionId: number) => void,
  setTypeOption: (typeOption: string) => void,
  slideObject: slideObjectPropTypes,
  typeOption: string,
  unitId?: number,
};

const BASE_OPTIONS = [
  { label: 'Unit Project', value: 'Unit Project' },
  { label: 'Activity Notebook', value: 'Activity Notebook' },
  { label: 'Reading Notebook', value: 'Section' },
];

const QuestionSelectFields = ({
  exerciseId,
  lessonId,
  modelId,
  modelType,
  programId,
  sectionId,
  setExerciseId,
  setHasError,
  setSectionId,
  setTypeOption,
  slideObject,
  typeOption,
  unitId,
}: QuestionSelectFieldsProps) => {
  const slideShowId = useSlideShowContext(state => state.slideShowId);
  const { bigBookSlideshow } = useFlags();
  const readingCheckOption = { label: 'Reading Check', value: 'Reading Check' };
  const [baseOptions, setBaseOptions] = useState([
    ...BASE_OPTIONS,
    ...(bigBookSlideshow ? [readingCheckOption] : []),
  ]);

  const params = new URLSearchParams([
    ['id', slideShowId.toString()],
    ['model_id', modelId],
    ['model_type', modelType],
  ]);

  const { data, isLoading } = useSlideShowQuestionGroups({ params });

  useEffect(() => {
    if (data.length === 0) return;

    const shouldAddThisSlideshow = data.some(qg => qg.questions.length > 0);

    const options = [
      ...BASE_OPTIONS,
      ...(bigBookSlideshow ? [readingCheckOption] : []),
      ...(shouldAddThisSlideshow ? [{ label: 'This Slideshow', value: 'Slide Show' }] : []),
    ];
    setBaseOptions(options);

    if (!shouldAddThisSlideshow) return;
    if (!slideObject || !modelId) return;

    const htmlElement = toCamelCaseDeep(slideObject.htmlElements
      .find(e => parseInt(e.id, 10) === parseInt(modelId, 10)));

    if (!Object.keys(htmlElement).length) return;

    const exerciseType = htmlElement.questionGroups?.[0]?.exerciseType;

    if (!exerciseType) {
      setTypeOption('Section');
      return;
    }

    if (exerciseType === 'Notebook') {
      const notebook = htmlElement.questionGroups[0]?.notebook;

      const notebookModelTypes = {
        Lesson: 'Activity Notebook',
        Section: 'Section',
        Unit: 'Unit Project',
      };

      setTypeOption(notebookModelTypes[notebook.modelType]);
    }
    if (exerciseType === 'ReadingGame') setTypeOption('Reading Check');
    if (exerciseType === 'SlideShow') setTypeOption('Slide Show');
  }, [data.length, isLoading, bigBookSlideshow]);

  const handleTypeChange = (e) => {
    setTypeOption(e.value);
    // Reset exerciseId when type changes to prevent using wrong ID
    setExerciseId(0);
  };

  return (
    <>
      <div className={styles.slideQuestionsSelects}>
        <div className={`${styles.modelSelect}`}>
          <label htmlFor="type-select">
            <p>Type:</p>
          </label>
          <Select
            className={`${styles.select} model-select`}
            id="type-select"
            label="type-select"
            options={baseOptions}
            value={typeOption}
            onChange={handleTypeChange}
          />
        </div>
        {typeOption === 'Unit Project' && (
          <UnitNotebookSelects
            exerciseId={exerciseId}
            lessonId={lessonId}
            programId={programId}
            setExerciseId={setExerciseId}
            setHasError={setHasError}
            unitId={unitId}
          />
        )}
        {typeOption === 'Section' && (
          <SectionNotebookSelects
            exerciseId={exerciseId}
            lessonId={lessonId}
            programId={programId}
            sectionId={sectionId}
            setExerciseId={setExerciseId}
            setHasError={setHasError}
            setSectionId={setSectionId}
            slideObject={slideObject}
            unitId={unitId}
          />
        )}
        {typeOption === 'Activity Notebook' && (
          <LessonNotebookSelects
            exerciseId={exerciseId}
            lessonId={lessonId}
            setExerciseId={setExerciseId}
            setHasError={setHasError}
            unitId={unitId}
          />
        )}
        {typeOption === 'Reading Check' && (
          <ReadingCheckSelects
            exerciseId={exerciseId}
            lessonId={lessonId}
            setExerciseId={setExerciseId}
            setHasError={setHasError}
          />
        )}
      </div>
    </>
  );
};

export default QuestionSelectFields;
